import _ from 'lodash'
import createWorkflowManager from '../../utils/workflow'
import { basicContext } from '../../utils/contextUtils'
import Errors from '../../utils/Errors'

/*
* Setup the workflow using the library
* */

export const workflowParameters = {
    steps: [
        {
            name: 'pending',
            actions: [{ name: 'validate', nextStep: 'inProgress' }]
        },
        {
            name: 'review',
            actions: [{ name: 'validate', nextStep: 'inProgress' }]
        },
        {
            name: 'inProgress',
            multiOrder: true,
            actions: [
                { name: 'validate', nextStep: 'noRisk' },
                { name: 'refuse', nextStep: 'risk', ignoreOrder: true },
                { name: 'question', nextStep: 'review', ignoreOrder: true }
            ]
        },
        { name: 'risk' },
        { name: 'noRisk' }
    ],
    profiles: [
        {
            name: 'manager',
            stepProfiles: [
                { step: 'pending', active: true },
                { step: 'review', active: true },
                { step: 'inProgress', active: false },
                { step: 'risk', active: false },
                { step: 'noRisk', active: false }
            ]
        },
        {
            name: 'validator',
            stepProfiles: [
                { step: 'inProgress', active: true },
                { step: 'risk', active: false },
                { step: 'noRisk', active: false }
            ]
        },
        {
            name: 'controller',
            stepProfiles: [
                { step: 'pending', active: false },
                { step: 'review', active: false },
                { step: 'inProgress', active: false },
                { step: 'risk', active: false },
                { step: 'noRisk', active: false }
            ],
            optional: true
        }
    ]
}

export const workflowManager = createWorkflowManager(workflowParameters)

const stepTKeys = {
    pending: 'pending',
    review: 'review',
    inProgress: 'inProgress',
    risk: 'risk',
    noRisk: 'noRisk'
}

export function getStepTkey(step) {
    return stepTKeys[step]
}

/*
* Gets the workflow configs corresponding to the alertConfiguration
* */
export function getWorkflowConfigs(alertConfigurationId, context) {
    if (!_.get(context, 'group')) throw new Errors.ValidationError('context without group')

    // add dormant check here to by-pass workflow config

    const workflowConfigContext = {
        group: context.group,
        fieldPath: [
            'id',
            'habFunction.id'
        ],
        query: {
            alertConfiguration: new global.ObjectID(alertConfigurationId),
            dormant: false,
        }
    }

    return global.app.S.WorkflowConfig.find(workflowConfigContext)
}

/*
* Generates the default object for the workflow configs
* */
export function getWorkflowObject(workflowConfigs) {
    if (!workflowConfigs || !workflowConfigs.length) return null

    const initialStatus = workflowManager.getInitialStatus()
    const maxOrders = {
        inProgress: _(workflowConfigs)
            .map('order')
            .max()
    }

    return {
        ...initialStatus,
        maxOrders
    }
}

/*
* Projects the workflow configs creating the static workflow that will be saved.
*
* The workflow shape (which HabFunction is active at which step/order) is generated once,
* at alert creation time, and never regenerated even if the WorkflowConfig changes afterwards.
* User resolution ("who currently holds this function, in scope for this alert") is NOT
* materialized here: it's resolved live, at read time, from HabFunction.habilitations
* (see resolveUsersForHabFunctions below).
*
* StaticWorkflow = {
*   step: String,
*   order: Number,
*   active: Boolean,
*   habFunction: HabFunction
* }
*
* */
export function initializeWorkflowData(workflowObject, workflowConfigs) {
    const orders = _.range(1, workflowObject.maxOrders.inProgress + 1)

    const workflowUnits = _(workflowConfigs)
        .flatMap(config =>
            _.flatten(getStaticWorkflowObjectsForProfile(config, orders)).map(
                staticWorkflow => ({
                    ...staticWorkflow,
                    habFunction: config.habFunction
                })
            )
        )
        .value()

    // the same HabFunction may appear in several WorkflowConfig rows (e.g. both
    // manager and validator for the same alert type) : merge them by (habFunction, step, order),
    // a merged row is active if any of the merged profiles was active
    return _(workflowUnits)
        .groupBy(({ habFunction, step, order }) => `${habFunction.id}-${step}-${order}`)
        .map(units => ({
            step: units[0].step,
            order: units[0].order,
            habFunction: units[0].habFunction,
            active: units.some(unit => unit.active)
        }))
        .value()
}

/*
* Generates workflow objects for a given profile
*
* For the multi-order intervention step :
*  - the manger profile is active on pending and review_ and passive on others
*  - the validator profile is active on it's order, and passive on the next orders
*  - the controller profile is passive on risk and noRisk
* */
const getStaticWorkflowObjectsForProfile = (config, orders) => {
    switch (config.profile) {
        case 'manager':
            return [
                { step: 'pending', order: 0, active: true },
                { step: 'review', order: 0, active: true },
                ...orders.map(order => ({
                    step: 'inProgress',
                    order,
                    active: false
                })),
                { step: 'risk', order: 0, active: false },
                { step: 'noRisk', order: 0, active: false }
            ]
        case 'validator':
            return [
                ...orders.reduce((acc, order) => {
                    return order < config.order
                        ? acc
                        : [
                            ...acc,
                            {
                                step: 'inProgress',
                                order,
                                active: order === config.order
                            }
                        ]
                }, []),
                { step: 'risk', order: 0, active: false },
                { step: 'noRisk', order: 0, active: false }
            ]
        case 'controller':
            return [
                { step: 'pending', order: 0, active: false },
                { step: 'review', order: 0, active: false },
                ...orders.map(order => ({
                    step: 'inProgress',
                    order,
                    active: false
                })),
                { step: 'risk', order: 0, active: false },
                { step: 'noRisk', order: 0, active: false }
            ]
        default:
            return
    }
}

/*
* Matches the habilitation with a given alert
* We have three cases :
*  - no organizationJoin -> central habilitation -> have access
*  - organizationJoin is an instance of Store -> access if matches with the store of the alert
*  - organizationJoin is an instance of Organization -> access if matches with one of the store organizations
* */
const habilitationMatches = alert => habilitation => {
    const checkOrganizations =
        habilitation.organizationsJoin

    return (
        (!checkOrganizations || checkOrganizations.length === 0) ||
        (alert.store && checkOrganizations.some(org => org.id === alert.store.id)) ||
        (alert.organizations &&
            alert.organizations.some(
                organization => checkOrganizations.some(org => organization.id === org.id)
            ))
    )
}

/*
* Resolves the live set of active users for a list of HabFunctions, in the scope of a given alert.
*
* This is the mechanism that replaces the old StatusUser materialized cache: instead of syncing
* a per-user row on every workflow transition / habilitation change, we resolve HabFunction.habilitations
* live, at read time, org-matched against the alert and filtered to active users.
* */
function resolveUsersForHabFunctions(habFunctions, alert) {
    return _(habFunctions)
        .flatMap('habilitations')
        .filter(habilitationMatches(alert))
        .map('user')
        .filter(user => user && user.active === true)
        .uniqBy('id')
        .value()
}

function resolveUsersFromStaticWorkflows(staticWorkflows, alert) {
    return resolveUsersForHabFunctions(_.map(staticWorkflows, 'habFunction'), alert)
}

// withUsers joins every Habilitation of the HabFunction (one per habilitated user) with its
// organizations and user: only ask for it when the users are really needed, it is expensive.
const staticWorkflowJoinFieldPath = (extraUserFields = [], extraHabFunctionFields = [], withUsers = true) => [
    'habFunction.id',
    ...(withUsers ? [
        'habFunction.habilitations.id',
        'habFunction.habilitations.organizationsJoin.id',
        'habFunction.habilitations.user.id',
        'habFunction.habilitations.user.active',
        ...extraUserFields.map(field => `habFunction.habilitations.user.${field}`)
    ] : []),
    ...extraHabFunctionFields.map(field => `habFunction.${field}`)
]

function getStaticWorkflowsForAlert(alert, context, { query = {}, extraUserFields = [], extraHabFunctionFields = [], withUsers = true } = {}) {
    const staticWorkflowContext = {
        ...basicContext(context),
        query: {
            alert: new global.ObjectID(alert.id),
            ...query
        },
        fieldPath: staticWorkflowJoinFieldPath(extraUserFields, extraHabFunctionFields, withUsers)
    }

    return global.app.S.StaticWorkflow.find(staticWorkflowContext)
}

/*
* Get a string describing the current status of the workflow
* */
export function getStatusString({ workflow }, context) {
    if (!workflow) {
        console.warn('object without workflow!')
        return 'no workflow'
    }

    const stepTitle = context.tc(getStepTkey(workflow.step))
    return _.compact([
        stepTitle,
        workflow.order > 0 ? workflow.order : undefined
    ]).join(' / ')
}

function getCurrentStepOrder({ step, order }) {
    switch (step) {
        case 'pending':
        case 'review':
            return 1
        case 'inProgress':
            return order + 1
        default:
            return 0
    }
}

export function getStepStatus({ workflow }) {
    if (!workflow) {
        console.warn('object without workflow!')
        return 'no workflow'
    }

    // for the finished steps, we return a special string
    if (['risk', 'noRisk'].includes(workflow.step)) return '-'

    const totalSteps = workflow.maxOrders.inProgress + 1

    return `${getCurrentStepOrder(workflow)}/${totalSteps}`
}

/**
 * Get All users ever involved in the static workflow of the alert (any step/order, any active state)
 * @param alert
 * @param context
 * @returns {Promise<Array>}
 */
export async function getUsersInStaticWorkflow(alert, context) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        extraUserFields: ['mail']
    })

    return resolveUsersFromStaticWorkflows(staticWorkflows, alert)
}

/**
 * Get list of current users for the alert (active HabFunctions at the alert's current step/order)
 * @param alert
 * @param context
 * @returns {Promise<*>}
 */
export async function getCurrentUsers(alert, context) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        query: {
            active: true,
            step: alert.workflow.step,
            order: alert.workflow.order
        }
    })

    return resolveUsersFromStaticWorkflows(staticWorkflows, alert)
}

/**
 * Get all users involved (active or not) at the alert's current step/order
 * @param alert
 * @param context
 * @param extraUserFields
 * @returns {Promise<Array>}
 */
export async function getInvolvedUsersForCurrentStep(alert, context, extraUserFields = []) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        query: {
            step: alert.workflow.step,
            order: alert.workflow.order
        },
        extraUserFields
    })

    return resolveUsersFromStaticWorkflows(staticWorkflows, alert)
}

/**
 * Get list of next users for the alert
 * @param alert
 * @param context
 * @returns {Promise<ReactWrapper|ShallowWrapper|Array|*|any[]>}
 */
export async function getNextUsers(alert, context) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        query: { active: true }
    })

    const nextStaticWorkflows = staticWorkflows.filter(staticWorkflow => {
        if (['pending', 'review'].includes(alert.workflow.step)) {
            return staticWorkflow.step === 'inProgress'
        } else if (alert.workflow.step === 'inProgress') {
            return staticWorkflow.order > alert.workflow.order
        }
        return false
    })

    return resolveUsersFromStaticWorkflows(nextStaticWorkflows, alert)
}

/*
* Get a string with the names of all the active HabFunctions for the alert
* */
export async function getAlertHabFunctions(alert, context) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        query: {
            active: true,
            step: alert.workflow.step,
            order: alert.workflow.order
        },
        extraHabFunctionFields: ['tName'],
        withUsers: false
    })

    return _(staticWorkflows)
        .map('habFunction.tName')
        .uniq()
        .join(', ')
}

/*
* Same as getAlertHabFunctions, for a list of alerts, in two queries instead of one per alert.
* Returns a Map: alert id -> string with the names of the active HabFunctions
* */
export async function getAlertsHabFunctions(alerts, context) {
    const result = new Map(alerts.map(alert => [alert.id, '']))
    if (!alerts.length) return result

    const alertsById = _.keyBy(alerts, 'id')

    const staticWorkflows = await global.app.S.StaticWorkflow.collection
        .find(
            {
                group: new global.ObjectID(context.group.id),
                alert: { $in: alerts.map(alert => new global.ObjectID(alert.id)) },
                active: true
            },
            { projection: { alert: 1, habFunction: 1, step: 1, order: 1 } }
        )
        .toArray()

    // only keep the rows at the alert's current step/order
    const currentStaticWorkflows = staticWorkflows.filter(staticWorkflow => {
        const workflow = _.get(alertsById[staticWorkflow.alert.toString()], 'workflow')
        return workflow && staticWorkflow.step === workflow.step && staticWorkflow.order === workflow.order
    })

    const habFunctionIds = _.uniqBy(currentStaticWorkflows.map(sw => sw.habFunction), id => id.toString())
    if (!habFunctionIds.length) return result

    const habFunctions = await global.app.S.HabFunction.find({
        ...basicContext(context),
        query: { _id: { $in: habFunctionIds } },
        fieldPath: ['id', 'tName']
    })
    const habFunctionNames = new Map(habFunctions.map(habFunction => [habFunction.id, habFunction.tName]))

    _(currentStaticWorkflows)
        .groupBy(staticWorkflow => staticWorkflow.alert.toString())
        .forEach((staticWorkflows, alertId) => {
            result.set(alertId, _(staticWorkflows)
                .map(staticWorkflow => habFunctionNames.get(staticWorkflow.habFunction.toString()))
                .compact()
                .uniq()
                .join(', '))
        })

    return result
}

/*
* Get the alert's active step, when there is a currently active HabFunction for it
* */
export async function getActiveStep(alert, context) {
    const staticWorkflows = await getStaticWorkflowsForAlert(alert, context, {
        query: {
            active: true,
            step: alert.workflow.step,
            order: alert.workflow.order
        },
        withUsers: false
    })

    return staticWorkflows.length ? alert.workflow.step : ""
}

/*
* Aggregation expression matching habilitationMatches' logic (org scope of the Habilitation
* being looked up, referenced as $organizationsJoin) against a given alert's store/organizations,
* passed in as aggregation expressions (e.g. "$$alertStore" / "$$alertOrganizations" let-vars).
* */
export const habilitationOrgMatchExpr = (storeExpr, organizationsExpr) => ({
    $or: [
        { $eq: [{ $size: { $ifNull: ['$organizationsJoin', []] } }, 0] },
        { $in: [storeExpr, { $ifNull: ['$organizationsJoin', []] }] },
        {
            $gt: [
                {
                    $size: {
                        $setIntersection: [
                            { $ifNull: ['$organizationsJoin', []] },
                            { $ifNull: [organizationsExpr, []] }
                        ]
                    }
                },
                0
            ]
        }
    ]
})

/**
 * Get the ids of the alerts for which the current user holds a HabFunction in the StaticWorkflow,
 * in scope for that alert (org-matched, same predicate as getCurrentUsers), at the alert's
 * current step/order (mirrors the old StatusUser semantics, which only ever materialized rows
 * for the alert's current position).
 *
 * extraMatch is applied directly on the StaticWorkflow rows (e.g. { active: true }, { step: 'risk' },
 * or a date range) before resolving to the alert's current position.
 * @param context
 * @param extraMatch
 * @returns {Promise<Array>}
 */
export async function getAlertIdsForUserStaticWorkflow(context, extraMatch = {}) {
    const alertCollectionName = global.app.S.Alert.collectionName
    const habilitationCollectionName = global.app.S.Habilitation.collectionName

    const rows = await global.app.S.StaticWorkflow.collection.aggregate([
        {
            $match: {
                group: new global.ObjectID(context.group.id),
                ...extraMatch
            }
        },
        {
            $lookup: {
                from: alertCollectionName,
                localField: 'alert',
                foreignField: '_id',
                as: 'alertDetails'
            }
        },
        { $unwind: '$alertDetails' },
        {
            $match: {
                $expr: {
                    $and: [
                        { $eq: ['$step', '$alertDetails.workflow.step'] },
                        { $eq: ['$order', '$alertDetails.workflow.order'] }
                    ]
                }
            }
        },
        {
            $lookup: {
                from: habilitationCollectionName,
                let: {
                    habFunctionId: '$habFunction',
                    alertStore: '$alertDetails.store',
                    alertOrganizations: '$alertDetails.organizations'
                },
                pipeline: [
                    {
                        $match: {
                            $expr: {
                                $and: [
                                    { $eq: ['$habFunction', '$$habFunctionId'] },
                                    { $eq: ['$user', new global.ObjectID(context.user.id)] },
                                    habilitationOrgMatchExpr('$$alertStore', '$$alertOrganizations')
                                ]
                            }
                        }
                    }
                ],
                as: 'hab'
            }
        },
        {
            $match: {
                'hab.0': { $exists: true }
            }
        },
        {
            $group: { _id: '$alert' }
        }
    ]).toArray()

    return rows.map(row => row._id)
}

/**
 * Get all alerts on user's scope
 * @param user
 * @param period
 * @param context
 * @returns {*}
 */
export function getUserClosedAlerts(user, period, context) {
    const alertCollectionName = global.app.S.Alert.collection
    const habFunctionCollectionName = global.app.S.HabFunction.collectionName
    const habilitationCollectionName = global.app.S.Habilitation.collectionName
    const staticWorkflowCollection = global.app.S.StaticWorkflow.collectionName

    return alertCollectionName
        .aggregate([
            {
                $match: {
                    'workflow.step': {$in: ['risk', 'noRisk']},
                    'date.gte': { $lte: period.end },
                    'date.lt': { $gt: period.start },
                    group: new global.ObjectID(context.group.id),
                }
            },
            {
                $lookup: {
                    from: staticWorkflowCollection,
                    localField: '_id',
                    foreignField: 'alert',
                    as: 'staticWFs'
                }
            },
            {
                $lookup: {
                    from: habFunctionCollectionName,
                    localField: 'staticWFs.habFunction',
                    foreignField: '_id',
                    as: 'habFunctions'
                }
            },
            {
                $lookup: {
                    from: habilitationCollectionName,
                    let: {
                        habFunctionIds: '$habFunctions._id',
                        alertStore: '$store',
                        alertOrganizations: '$organizations'
                    },
                    pipeline: [
                        {
                            $match: {
                                $expr: {
                                    $and: [
                                        { $in: ['$habFunction', '$$habFunctionIds'] },
                                        { $eq: ['$user', new global.ObjectID(user.id)] },
                                        habilitationOrgMatchExpr('$$alertStore', '$$alertOrganizations')
                                    ]
                                }
                            }
                        }
                    ],
                    as: 'habilitations'
                }
            },
            {
                $match: {
                    'habilitations.0': { $exists: true }
                }
            },
            {
                $project: {
                    _id: 1,
                    'date': 1,
                    workflow: 1,
                    alertConfiguration: 1,
                    organizations: 1,
                    product: 1,
                    store: 1,
                    group: 1
                }
            }
        ])
        .toArray()
}

/*
* Defines the bootstrap button style for the action
* */
function bsStyleForAction(action) {
    switch (action) {
        case 'refuse':
            return 'danger'
        case 'question':
            return 'warning'
        case 'validate':
        default:
            return 'primary'
    }
}

function getActiveButtonsForStep(step) {
    const stepObject =
        workflowParameters.steps.find(stepObject => stepObject.name === step) ||
        {}
    const actions = stepObject.actions || []
    return actions.map(action => ({
        action: action.name,
        bsStyle: bsStyleForAction(action.name),
        tKey: action.name,
        type: 'action'
    }))
}

/*
* Determines the current user's involvement in the alert's workflow, at its current step/order :
*  - { active: true } if the user holds a HabFunction that is currently active for the alert
*  - { active: false } if the user holds a HabFunction involved but only passively at this step/order
*  - null if the user has no involvement at the alert's current step/order at all
* */
export async function getUserInvolvementForAlert(alert, context) {
    const userId = _.get(context, 'user.id')
    if (!userId) return null

    const staticWorkflows = await global.app.S.StaticWorkflow.collection
        .find(
            {
                group: new global.ObjectID(context.group.id),
                alert: new global.ObjectID(alert.id),
                step: alert.workflow.step,
                order: alert.workflow.order
            },
            { projection: { habFunction: 1, active: 1 } }
        )
        .toArray()
    if (!staticWorkflows.length) return null

    // only the current user's habilitations on those HabFunctions: resolving every habilitated
    // user of the HabFunctions (with their organizations) is far too expensive for one alert
    const habilitations = await global.app.S.Habilitation.collection
        .find(
            {
                habFunction: { $in: _.uniqBy(staticWorkflows.map(sw => sw.habFunction), id => id.toString()) },
                user: new global.ObjectID(userId)
            },
            { projection: { habFunction: 1, organizationsJoin: 1 } }
        )
        .toArray()

    const userHabFunctionIds = new Set(
        habilitations
            .filter(habilitation => habilitationMatches(alert)({
                organizationsJoin: (habilitation.organizationsJoin || []).map(ref => ({ id: (ref.oid || ref).toString() }))
            }))
            .map(habilitation => habilitation.habFunction.toString())
    )
    const userHoldsOneOf = rows => rows.some(sw => userHabFunctionIds.has(sw.habFunction.toString()))

    if (userHoldsOneOf(staticWorkflows.filter(sw => sw.active))) {
        return { active: true, step: alert.workflow.step }
    }
    if (userHoldsOneOf(staticWorkflows.filter(sw => !sw.active))) {
        return { active: false, step: alert.workflow.step }
    }
    return null
}

/*
* Get workflow buttons for a given involvement (see getUserInvolvementForAlert).
*
* Passive/uninvolved users only get the return button.
* Users with an active involvement get one button per step action, plus return.
* */
export function getAlertButtonsForInvolvement(involvement) {
    return _([
        involvement && involvement.active && getActiveButtonsForStep(involvement.step),
        {
            tKey: 'return',
            bsStyle: 'default',
            type: 'return'
        }
    ])
        .flatten()
        .compact()
        .value()
}

/*
* Get workflow buttons for the current user on this alert.
* */
export async function getAlertButtons(alert, context) {
    const involvement = await getUserInvolvementForAlert(alert, context)

    return getAlertButtonsForInvolvement(involvement)
}
